import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouteContext } from '@tanstack/react-router';
import { Avatar, Button, Flex, Typography, Upload, message } from 'antd';
import { Upload as UploadIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  getListCustomFieldsQueryKey,
  getListImportJobsQueryKey,
  useCreateImportJob,
  useCreateImportUploadUrl,
  useGetImportJob,
} from '../../../../api/generated/api';
import {
  ANALYZING_STATUSES,
  ImportAnalyzing,
  ImportFileCard,
  ImportLayout,
} from '../../../../modules/import';

const NewImportRoute = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedOrganization } = useRouteContext({ from: '/_authenticated/_app' });
  const [file, setFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const { mutateAsync: createUploadUrl } = useCreateImportUploadUrl();
  const { mutateAsync: createJob } = useCreateImportJob();

  const { mutate: upload, isPending } = useMutation({
    mutationFn: async (picked: File) => {
      const { data: target } = await createUploadUrl({
        organizationId: selectedOrganization.id,
        data: { fileName: picked.name, fileSize: picked.size },
      });

      const uploaded = await fetch(target.uploadUrl, {
        method: target.method,
        headers: { 'Content-Type': target.contentType },
        body: picked,
      });

      if (!uploaded.ok) {
        throw new Error(`Upload failed with status ${uploaded.status}`);
      }

      const { data: createdJob } = await createJob({
        organizationId: selectedOrganization.id,
        data: { fileName: picked.name, storagePath: target.storagePath },
      });

      return createdJob;
    },
    onSuccess: (createdJob) => {
      setJobId(createdJob.id);
      queryClient.invalidateQueries({
        queryKey: getListImportJobsQueryKey(selectedOrganization.id),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomFieldsQueryKey(selectedOrganization.id),
      });
    },
    onError: () => message.error('Failed to upload the CSV'),
  });

  const { data: jobData } = useGetImportJob(selectedOrganization.id, jobId ?? '', {
    query: { enabled: jobId != null },
  });

  const job = jobData?.data;

  useEffect(() => {
    if (job && !ANALYZING_STATUSES.includes(job.status)) {
      navigate({ to: '/import/$importJobId', params: { importJobId: job.id } });
    }
  }, [job, navigate]);

  const handleUpload = () => {
    if (!file) {
      return;
    }

    upload(file);
  };

  if ((isPending || jobId) && file) {
    return (
      <ImportLayout subtitle={file.name} currentStep={1}>
        <ImportAnalyzing fileName={file.name} onViewHistory={() => navigate({ to: '/import' })} />
      </ImportLayout>
    );
  }

  return (
    <ImportLayout
      subtitle={file ? file.name : 'Bring records into your CRM'}
      currentStep={0}
      footer={
        <>
          <Button disabled={isPending} onClick={() => navigate({ to: '/import' })}>
            Cancel
          </Button>
          <Button
            type="primary"
            data-testid="import-continue"
            disabled={!file}
            loading={isPending}
            onClick={handleUpload}
          >
            Continue
          </Button>
        </>
      }
    >
      {file ? (
        <ImportFileCard file={file} onRemove={() => setFile(null)} />
      ) : (
        <div data-testid="import-upload">
          <Upload.Dragger
            accept=".csv,text/csv"
            multiple={false}
            showUploadList={false}
            style={{ borderRadius: 12 }}
            beforeUpload={(picked) => {
              if (!picked.name.toLowerCase().endsWith('.csv')) {
                message.error('Only .csv files are accepted');
                return Upload.LIST_IGNORE;
              }

              if (picked.size > 200 * 1024 * 1024) {
                message.error('The file must be 200 MB or smaller');
                return Upload.LIST_IGNORE;
              }

              setFile(picked);
              return false;
            }}
          >
            <Flex vertical align="center" gap={12} style={{ padding: '40px 20px' }}>
              <Avatar
                size={46}
                style={{ backgroundColor: 'var(--ant-color-primary)' }}
                icon={<UploadIcon size={20} />}
              />
              <Flex vertical gap={5}>
                <Typography.Text strong style={{ fontSize: 14 }}>
                  Drop your CSV here or <Typography.Link>browse</Typography.Link>
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  Up to 200 MB · UTF-8 encoded .csv
                </Typography.Text>
              </Flex>
            </Flex>
          </Upload.Dragger>
        </div>
      )}
    </ImportLayout>
  );
};

export const Route = createFileRoute('/_authenticated/_app/import/new')({
  component: NewImportRoute,
});
